import React, { useEffect, useState, useCallback, useRef, type JSX } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff, Phone, Share, Voicemail, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@core-ui/Components/ui/card';
import { Button } from '@core-ui/Components/ui/button';

const DIALPAD_BUTTONS = [
  { main: '1', sub: '' },
  { main: '2', sub: 'ABC' },
  { main: '3', sub: 'DEF' },
  { main: '4', sub: 'GHI' },
  { main: '5', sub: 'JKL' },
  { main: '6', sub: 'MNO' },
  { main: '7', sub: 'PQRS' },
  { main: '8', sub: 'TUV' },
  { main: '9', sub: 'WXYZ' },
  { main: '*', sub: '' },
  { main: '0', sub: '+' },
  { main: '#', sub: '' },
];

const COOLDOWN_TIMEOUT = 2000;

interface Props {
  number?: string;
  active?: boolean;

  onCallStart?: (num: string) => void;
  onCallEnd?: () => void;
  onSendDTMF?: (digit: string) => void;

  muted?: boolean;
  onToggleMute?: (next: boolean) => void;
  onTransfer?: () => void;
  onPlayVoicemail?: () => void | Promise<void>;
  hasVoicemail?: boolean;
  isTransferred?: boolean;
  setIsTransferred?: (value: boolean) => void,
  activeVoicemail: boolean,
  setActiveVoicemail?: (value: boolean) => void,
  disabled?: boolean;
  showTransfer?: boolean;
  isSDR?: boolean;
}

const Dialpad = ({
  number = '',
  active = false,

  onCallStart,
  onCallEnd,
  onSendDTMF,

  muted = false,
  onToggleMute,
  onTransfer,
  onPlayVoicemail,
  activeVoicemail,
  hasVoicemail = true,
  isTransferred = false,

  disabled = false,
  showTransfer = true,
  isSDR = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();

  const [input, setInput] = useState<string>(number);
  const [seconds, setSeconds] = useState(0);
  const [isPlayingVm, setIsPlayingVm] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [localTransferred, setLocalTransferred] = useState(isTransferred ?? false);
  const [transferCooldown, setTransferCooldown] = useState(false);
  const [voicemailCooldown, setVoicemailCooldown] = useState(false);
  const prevVoicemailRef = useRef(activeVoicemail);

  // Effective lock status
  const isDisabled = Boolean(disabled);

  useEffect(() => {
    setInput(number);
  }, [number]);

  useEffect(() => {
    if (!active) {
      return;
    }
    const tId = window.setInterval(() => {
      setSeconds((s) => s + 1);
    }, 1000);
    return () => {
      window.clearInterval(tId);
    };
  }, [active]);

  const formatTime = (total: number): string => {
    const m = Math.floor(total / 60).toString().padStart(2, '0');
    const s = (total % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const insertAtCursor = useCallback((text: string) => {
    if (isDisabled) {
      return;
    }
    const el = inputRef.current;
    if (el == null) {
      setInput((v) => v + text);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + text + el.value.slice(end);
    setInput(next);
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + text.length;
      el.setSelectionRange(caret, caret);
    });
  }, [isDisabled]);

  const handlePress = useCallback(
    (digit: string) => {
      if (isDisabled) {
        return;
      }
      if (active && (onSendDTMF != null)) {
        onSendDTMF(digit);
      }
      insertAtCursor(digit);
    },
    [active, onSendDTMF, insertAtCursor, isDisabled]
  );

  const toggleCall = useCallback(() => {
    if (isDisabled) {
      return;
    }

    if (active) {
      onCallEnd?.();
      setSeconds(0);
      return;
    }
    const num = input.trim() !== '' ? input.trim() : number.trim();
    onCallStart?.(num);
    setSeconds(0);
  }, [active, input, number, onCallEnd, onCallStart, isDisabled]);

  const handleToggleMute = useCallback(() => {
    if (isDisabled) {
      return;
    }
    onToggleMute?.(!muted);
  }, [muted, onToggleMute, isDisabled]);

  const handleVm = useCallback(async () => {
    if (isDisabled || onPlayVoicemail == null) {
      return;
    }
    try {
      setIsPlayingVm(true);
      await onPlayVoicemail();
    } finally {
      setIsPlayingVm(false);
    }
  }, [onPlayVoicemail, isDisabled]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (isDisabled) {
        return;
      }

      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;

      if (tag === 'INPUT' || tag === 'TEXTAREA' || ((target?.isContentEditable) === true)) {
        return;
      }

      const key = e.key;
      const isDTMF = /^[0-9]$/.test(key) || key === '*' || key === '#';

      if (isDTMF) {
        e.preventDefault();
        handlePress(key);
        return;
      }

      if (active && /^[A-Da-d]$/.test(key)) {
        e.preventDefault();
        onSendDTMF?.(key.toUpperCase());
        return;
      }

      switch (key) {
      case '+': {
        if (!active) {
          e.preventDefault();
          insertAtCursor('+');
        }
        break;
      }
      case 'Backspace': {
        e.preventDefault();
        const el = inputRef.current;
        if ((el != null) && document.activeElement === el) {
          const start = el.selectionStart ?? el.value.length;
          const end = el.selectionEnd ?? el.value.length;
          if (start === end && start > 0) {
            const next = el.value.slice(0, start - 1) + el.value.slice(end);
            setInput(next);
            requestAnimationFrame(() => {
              el.focus();
              const caret = start - 1;
              el.setSelectionRange(caret, caret);
            });
          } else {
            const next = el.value.slice(0, start) + el.value.slice(end);
            setInput(next);
            requestAnimationFrame(() => {
              el.focus();
              el.setSelectionRange(start, start);
            });
          }
        } else {
          setInput((v) => v.slice(0, -1));
        }
        break;
      }
      case 'Enter': {
        e.preventDefault();
        toggleCall();
        break;
      }
      case 'Escape': {
        if (active) {
          e.preventDefault();
          onCallEnd?.();
          setSeconds(0);
        }
        break;
      }
      case 'm':
      case 'M': {
        if (active) {
          e.preventDefault();
          handleToggleMute();
        }
        break;
      }
      case 'v':
      case 'V': {
        if (active && hasVoicemail && !isPlayingVm) {
          e.preventDefault();
          void handleVm();
        }
        break;
      }
      case 't':
      case 'T': {
        if (active && showTransfer && (onTransfer != null)) {
          e.preventDefault();
          onTransfer();
        }
        break;
      }
      default:
        break;
      }
    },
    [active, handlePress, toggleCall, onCallEnd, handleToggleMute, hasVoicemail, isPlayingVm, handleVm, onSendDTMF, insertAtCursor, isDisabled, showTransfer, onTransfer]
  );

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [onKeyDown]);

  useEffect(() => {
    if (prevVoicemailRef.current && !activeVoicemail) {
      setVoicemailCooldown(true);

      const timeout = window.setTimeout(() => {
        setVoicemailCooldown(false);
      }, COOLDOWN_TIMEOUT);

      return () => {
        clearTimeout(timeout);
      };
    }

    prevVoicemailRef.current = activeVoicemail;
  }, [activeVoicemail]);

  const handleTransfer = useCallback(() => {
    if (!active || isDisabled || activeVoicemail || transferCooldown || voicemailCooldown) {
      return;
    }

    setLocalTransferred(true);
    onTransfer?.();
    setTransferCooldown(true);

    window.setTimeout(() => {
      setTransferCooldown(false);
    }, COOLDOWN_TIMEOUT);
  }, [active, isDisabled, activeVoicemail, transferCooldown, onTransfer, voicemailCooldown]);

  return (
    <Card className={clsx([
      'bg-card rounded-2xl shadow-sm border border-border h-full flex flex-col min-h-0 overflow-hidden',
      isDisabled && 'opacity-60 pointer-events-none select-none',
      localTransferred && 'border-(--ring)',
      !isSDR && isTransferred && 'max-h-[550px]',
      activeVoicemail && 'border-(--purple)'
    ])}>
      <CardHeader className="text-center shrink-0 px-3 md:px-4 py-1.5 md:py-2">
        <CardTitle className="text-sm md:text-base text-foreground">
          {t('dialer.dialpad.title', 'Dial Pad')}
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col items-center gap-1.5 md:gap-2 px-3 md:px-4 pt-1 pb-2 md:pb-3 w-full flex-1 min-h-0 overflow-hidden">
        <input
          ref={inputRef}
          type="tel"
          inputMode="tel"
          value={input}
          onChange={(e) => {
            if (isDisabled) {
              return;
            }
            setInput(e.target.value);
          }}
          placeholder="+1"
          disabled={isDisabled}
          className={clsx(
            'w-full max-w-xs text-center font-mono tracking-wider',
            'text-lg md:text-xl 2xl:text-2xl bg-transparent outline-none',
            'border-b border-border pb-1 text-foreground'
          )}
          aria-label={t('dialer.dialpad.number', 'Phone number')}
        />

        {active && (
          <div className="text-xs font-medium text-muted-foreground -mt-1">
            {t('dialer.dialpad.callTime', 'Call time')}: {formatTime(seconds)}
          </div>
        )}

        <div className="grid grid-cols-3 gap-1 md:gap-1.5 2xl:gap-3">
          {DIALPAD_BUTTONS.map(({ main, sub }) => (
            <button
              key={main}
              onClick={() => {
                if (isDisabled) {
                  return;
                }
                handlePress(main);
              }}
              disabled={isDisabled}
              className={clsx(
                'size-10 2xl:size-14 rounded-full flex flex-col items-center justify-center mx-auto',
                'bg-background hover:bg-muted transition-colors text-foreground',
                'shadow-sm border border-border',
                isDisabled && 'hover:bg-background'
              )}
            >
              <span className="text-base md:text-lg 2xl:text-2xl font-semibold leading-none">{main}</span>
              {sub !== '' && (
                <span className="text-[9px] md:text-[10px] 2xl:text-xs leading-none text-muted-foreground">{sub}</span>
              )}
            </button>
          ))}
          <div />
          <button
            onClick={() => {
              if (isDisabled) {
                return;
              }
              toggleCall();
            }}
            disabled={isDisabled}
            className={clsx(
              'size-10 2xl:size-14 rounded-full flex items-center justify-center mx-auto cursor-pointer disabled:cursor-not-allowed',
              active
                ? 'bg-destructive text-white hover:bg-destructive/90'
                : 'bg-green-600 text-white hover:bg-green-700'
            )}
            aria-label={active ? t('dialer.dialpad.hangup', 'Hang up') : t('dialer.dialpad.call', 'Call')}
            title={active ? t('dialer.dialpad.hangup', 'Hang up') : t('dialer.dialpad.call', 'Call')}
          >
            <Phone className="size-5 md:size-6 2xl:size-7" />
          </button>
          <button
            onClick={() => {
              if (isDisabled) {
                return;
              }
              const el = inputRef.current;
              if ((el != null) && document.activeElement === el) {
                const evt = new KeyboardEvent('keydown', { key: 'Backspace' });
                window.dispatchEvent(evt);
              } else {
                setInput((v) => v.slice(0, -1));
              }
            }}
            disabled={isDisabled}
            className="h-7 md:h-8 px-2.5 md:px-3 flex items-center justify-center text-foreground bg-muted hover:text-destructive transition-all duration-200 ease-in-out hover:bg-foreground/20 hover:scale-[1.05] cursor-pointer disabled:cursor-not-allowed"
            aria-label={t('dialer.dialpad.delete', 'Delete')}
            title={t('dialer.dialpad.delete', 'Delete')}
            style={{
              clipPath: 'polygon(16% 0%, 100% 0%, 100% 100%, 16% 100%, 0% 50%)',
              borderRadius: '6px',
            }}
          >
            <X className="size-3.5 md:size-4" />
          </button>
        </div>

        <div className="flex items-center justify-center gap-6 md:gap-10 pt-3 md:pt-4 w-full">
          {showTransfer ? (
            <Button
              variant="ghost"
              size="icon"
              className={clsx(
                'cursor-pointer size-9 2xl:size-11 text-muted-foreground/70 hover:text-muted-foreground',
                localTransferred && 'text-(--ring)'
              )}
              disabled={!active || isDisabled || activeVoicemail || voicemailCooldown || transferCooldown}
              onClick={handleTransfer}
              aria-label={t('dialer.dialpad.transfer', 'Transfer')}
              title={t('dialer.dialpad.transferHotkey', 'Transfer (T)')}
            >
              <Share className="size-5 2xl:size-6" />
            </Button>
          ) : null}

          <Button
            variant="ghost"
            size="icon"
            className={clsx(
              'cursor-pointer size-9 2xl:size-11',
              activeVoicemail ? 'text-(--purple) hover:text-foreground' : 'text-muted-foreground/70 hover:text-muted-foreground'
            )}
            disabled={
              !active ||
                !hasVoicemail ||
                isPlayingVm ||
                isDisabled ||
                (!isSDR && localTransferred)}
            onClick={() => {
              if (isDisabled) {
                return;
              }
              void handleVm();
            }}
            aria-label={t('dialer.dialpad.voicemail', 'Voicemail')}
            title={t('dialer.dialpad.voicemailHotkey', 'Voicemail (V)')}
          >
            <Voicemail className="size-5 2xl:size-6" />
          </Button>

          {!showTransfer && (
            <div />
          )}

          <Button
            variant="ghost"
            size="icon"
            className={clsx(
              'cursor-pointer size-9 2xl:size-11',
              muted ? 'text-red-500' : 'text-muted-foreground/70 hover:text-muted-foreground'
            )}
            disabled={!active || isDisabled}
            onClick={handleToggleMute}
            aria-label={t('dialer.dialpad.toggleMute', 'Toggle mute')}
            aria-pressed={muted}
            title={muted ? t('dialer.dialpad.unmute', 'Unmute (M)') : t('dialer.dialpad.mute', 'Mute (M)')}
          >
            {muted ? <MicOff className="size-5 2xl:size-6" /> : <Mic className="size-5 2xl:size-6" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default Dialpad;
